import { dateUtils } from '@festina-life/core'
import { Alert } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { Stack } from '@festina-life/ui/structure'
import { Activity } from 'react'

import { FPlusScopePopover } from '../../components/fplusScope/FPlusScopePopover'
import { ModelDiagram } from '../../components/modelHierarchy/ModelDiagram'
import { SubTemplateControllableDiagram } from '../../components/subTemplateHierarchy/SubTemplateControllableDiagram'
import { InheritanceTimelineDiagram } from '../../components/templateTimeline/InheritanceTimelineDiagram'
import { useConfigurationContext } from '../../context/configurationContext'
import { useLayoutContext } from '../../context/layoutContext'
import { useTemplateContext } from '../../context/templateContext'
import { useTemplateId } from '../../hooks/useTemplateId'
import { useTemplateReferenceNode } from '../../uiModels/templateReference/useTemplateReferenceNode'
import { useTemplateVersionInfo } from '../../uiModels/versionInfo/useTemplateVersionInfo'
import { DiagramIconToggles } from './DiagramIconToggles'

/**
 * Sidebar component used for controlling the template view. Contains controls that will
 * tweak the template viewer configuration.
 */
export const TemplateSidebar = () => {
  const translate = useTranslate()
  const { templateVersion } = useTemplateContext()
  const { viewAt } = useConfigurationContext()
  const {
    preferences: {
      templates: {
        isShowingModelDiagram,
        isShowingInheritanceDiagram,
        isShowingSubTemplateDiagram,
      },
    },
  } = useLayoutContext()
  const templateId = useTemplateId()
  const versionInfoForHierarchy = useTemplateVersionInfo({
    id: templateId,
    date: viewAt,
  })
  const modelType = templateVersion?.modelType
  const templateReferenceNode = useTemplateReferenceNode({
    typeName: modelType?.typeName,
    at: dateUtils.dateAsServerStr(viewAt),
    id: templateId,
  })
  const canRenderHierarchyDiagrams = !!versionInfoForHierarchy
  const canRenderSubTemplateDiagrams = !!templateReferenceNode

  return (
    <div className="flex size-full flex-col gap-y-4 overflow-auto p-1">
      <Stack>
        <FPlusScopePopover path={`{${templateId}}`} />
        <DiagramIconToggles />
      </Stack>
      {!modelType && (
        <Alert
          severity="warning"
          title={translate('templateSidebar.typedDiagram.unavailableWarning')}
        />
      )}
      <Activity mode={isShowingModelDiagram ? 'visible' : 'hidden'}>
        {modelType ? <ModelDiagram rootType={modelType} /> : null}
      </Activity>
      <Activity mode={isShowingInheritanceDiagram ? 'visible' : 'hidden'}>
        {canRenderHierarchyDiagrams ? <InheritanceTimelineDiagram /> : null}
      </Activity>
      <Activity mode={isShowingSubTemplateDiagram ? 'visible' : 'hidden'}>
        {canRenderSubTemplateDiagrams ? (
          <SubTemplateControllableDiagram
            templateReferenceNode={templateReferenceNode}
          />
        ) : null}
      </Activity>
    </div>
  )
}
